import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Badge, Flex, Link, Tooltip } from '@optiaxiom/react';

import { useEvent } from '@frontend/hooks/useEvent';

import { calculateTagDisplay, getTagsToMeasure, type TagDisplayState } from './tag-display-utils';
import { styles } from './TagDisplay.css';

type TagDisplayProps = {
  tags: Array<{ name: string }>;
  quantity?: number;
};

export function TagDisplay({ tags, quantity }: TagDisplayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tagRefs = useRef<Array<HTMLElement | null>>([]);
  const counterRef = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [displayState, setDisplayState] = useState<TagDisplayState>({
    visibleCount: quantity ?? tags?.length ?? 0,
    lastTruncated: false,
  });

  // IntersectionObserver to only measure when component is visible
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '50px' } // Start measuring slightly before visible
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Use useEvent to get a stable reference without dependency tracking
  const performCalculation = useEvent(() => {
    if (!containerRef.current || !tags?.length) return;

    const containerWidth = containerRef.current.offsetWidth;
    if (containerWidth === 0) return;

    const newDisplayState = calculateTagDisplay({
      containerWidth,
      tags,
      quantity,
      tagRefs: tagRefs.current,
      counterRef: counterRef.current,
    });

    setDisplayState(newDisplayState);
  });

  useLayoutEffect(() => {
    // Only measure when visible in viewport
    if (!isVisible) return;

    performCalculation();
  }, [isVisible, performCalculation]);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Trigger re-calculation when container size changes
      performCalculation();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [performCalculation]);

  // Memoize expensive computations
  const tagsToMeasure = useMemo(
    () => getTagsToMeasure(quantity, tags?.length ?? 0),
    [quantity, tags?.length]
  );
  const remainingCount = useMemo(
    () => (tags?.length ?? 0) - displayState.visibleCount,
    [tags?.length, displayState.visibleCount]
  );

  const remainingTagsTooltip = useMemo(() => {
    if (remainingCount === 0) return '';
    return tags
      .slice(displayState.visibleCount)
      .map(tag => tag.name)
      .join(', ');
  }, [tags, displayState.visibleCount, remainingCount]);

  if (!tags || tags.length === 0) return null;

  return (
    <Flex
      ref={containerRef}
      flexDirection="row"
      gap="8"
      flexWrap="nowrap"
      overflow="hidden"
      flex="1"
      className={styles.container}
    >
      {tags.slice(0, tagsToMeasure).map((tag, index) => {
        const isVisible = index < displayState.visibleCount;
        const isLastVisibleTag = index === displayState.visibleCount - 1;
        const shouldTruncate = isVisible && isLastVisibleTag && displayState.lastTruncated;

        return (
          <Tooltip key={tag.name || `tag-${index}`} content={tag.name}>
            <Badge
              ref={el => {
                tagRefs.current[index] = el;
              }}
              intent="neutral"
              className={`${shouldTruncate ? styles.badgeTruncated : styles.badgeVisible} ${
                !isVisible ? styles.badgeHidden : ''
              }`}
            >
              <span className={shouldTruncate ? styles.badgeContentTruncated : styles.badgeContent}>
                {tag.name}
              </span>
            </Badge>
          </Tooltip>
        );
      })}
      {/* Always render counter for measurement - visible when there are remaining tags */}
      <Tooltip content={remainingTagsTooltip} disabled={remainingCount === 0}>
        <Link
          ref={counterRef as React.RefObject<HTMLAnchorElement>}
          className={styles.counterBadge[remainingCount > 0 ? 'visible' : 'hidden']}
        >
          {/* '00' is a placeholder to measure how much width the component takes up */}+
          {remainingCount > 0 ? remainingCount : '00'}
        </Link>
      </Tooltip>
    </Flex>
  );
}
